import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_URL = process.env.REACT_APP_API_URL;

// Async thunks for API calls
export const getResourcingForecast = createAsyncThunk(
  'resourcing/getForecast',
  async ({ startDate, endDate, teamFilter }, { rejectWithValue, signal }) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (teamFilter) params.append('teamFilter', teamFilter);

      const response = await fetch(`${API_URL}/api/resourcing/forecast?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return rejectWithValue(error.message);
    }
  }
);

export const getResourcingTeams = createAsyncThunk(
  'resourcing/getTeams',
  async (_, { rejectWithValue, signal }) => {
    try {
      const response = await fetch(`${API_URL}/api/resourcing/teams`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return rejectWithValue(error.message);
    }
  }
);

export const getResourcingSummary = createAsyncThunk(
  'resourcing/getSummary',
  async ({ startDate, endDate, teamFilter }, { rejectWithValue, signal }) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (teamFilter) params.append('teamFilter', teamFilter);

      const response = await fetch(`${API_URL}/api/resourcing/summary?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return rejectWithValue(error.message);
    }
  }
);

export const getResourcingBreakdown = createAsyncThunk(
  'resourcing/getBreakdown',
  async ({ startDate, endDate, teamFilter }, { rejectWithValue, signal }) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (teamFilter) params.append('teamFilter', teamFilter);

      const response = await fetch(`${API_URL}/api/resourcing/breakdown?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return rejectWithValue(error.message);
    }
  }
);

export const updateResourceAllocation = createAsyncThunk(
  'resourcing/updateAllocation',
  async ({ employeeId, allocatedHours, allocationWeek, userEmail }, { rejectWithValue, signal }) => {
    try {
      const response = await fetch(`${API_URL}/api/resourcing/allocation/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId,
          allocatedHours,
          allocationWeek,
          userEmail,
        }),
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  forecast: {
    headers: [],
    rows: [],
    summary: {
      total_people: 0,
      total_weeks: 0,
      date_range: ''
    }
  },
  breakdown: {
    headers: [],
    summary_rows: [],
    individual_rows: [],
    summary: {}
  },
  teams: [],
  summary: {
    total_people: 0,
    average_scheduled_hours: 0,
    total_scheduled_hours: 0,
    total_unscheduled_hours: 0,
    total_construction_queries: 0,
    date_range: ''
  },
  isLoading: false,
  isTeamsLoading: false,
  isSummaryLoading: false,
  isBreakdownLoading: false,
  isUpdating: false,
  error: null,
  updateMessage: null,
  filters: {
    startDate: null,
    endDate: null,
    teamFilter: 'NZ' // Default to NZ team
  }
};

const resourcingSlice = createSlice({
  name: 'resourcing',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearUpdateMessage: (state) => {
      state.updateMessage = null;
    },
    startLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Forecast
      .addCase(getResourcingForecast.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getResourcingForecast.fulfilled, (state, action) => {
        state.isLoading = false;
        state.forecast = action.payload;
      })
      .addCase(getResourcingForecast.rejected, (state, action) => {
        state.isLoading = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.error = action.payload;
      })
      
      // Get Teams
      .addCase(getResourcingTeams.pending, (state) => {
        state.isTeamsLoading = true;
      })
      .addCase(getResourcingTeams.fulfilled, (state, action) => {
        state.isTeamsLoading = false;
        state.teams = action.payload.data || [];
      })
      .addCase(getResourcingTeams.rejected, (state, action) => {
        state.isTeamsLoading = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.error = action.payload;
      })
      
      // Get Summary
      .addCase(getResourcingSummary.pending, (state) => {
        state.isSummaryLoading = true;
      })
      .addCase(getResourcingSummary.fulfilled, (state, action) => {
        state.isSummaryLoading = false;
        state.summary = action.payload.data || state.summary;
      })
      .addCase(getResourcingSummary.rejected, (state, action) => {
        state.isSummaryLoading = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.error = action.payload;
      })
      
      // Get Breakdown
      .addCase(getResourcingBreakdown.pending, (state) => {
        state.isBreakdownLoading = true;
      })
      .addCase(getResourcingBreakdown.fulfilled, (state, action) => {
        state.isBreakdownLoading = false;
        state.breakdown = action.payload.data || state.breakdown;
      })
      .addCase(getResourcingBreakdown.rejected, (state, action) => {
        state.isBreakdownLoading = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.error = action.payload;
      })
      
      // Update Allocation
      .addCase(updateResourceAllocation.pending, (state) => {
        state.isUpdating = true;
        state.updateMessage = null;
      })
      .addCase(updateResourceAllocation.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateMessage = action.payload;
      })
      .addCase(updateResourceAllocation.rejected, (state, action) => {
        state.isUpdating = false;
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearError, clearUpdateMessage, startLoading } = resourcingSlice.actions;

export default resourcingSlice.reducer; 